import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Network, Loader2, Play, CheckCircle2, XCircle, AlertTriangle, ChevronDown,
  Bot, Sparkles, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ICON = {
  completed: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />,
  running: <Loader2 className="w-3.5 h-3.5 text-orange-400 animate-spin" />,
  failed: <XCircle className="w-3.5 h-3.5 text-red-400" />,
  escalated: <AlertTriangle className="w-3.5 h-3.5 text-purple-400" />,
};

const ActivityRow = ({ act, agents }) => {
  const [open, setOpen] = useState(false);
  const label = agents?.[act.agent_id]?.label || act.agent_id;
  return (
    <div className="p-3 bg-zinc-800/40 rounded-xl" data-testid={`activity-${act.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left">
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_ICON[act.status] || STATUS_ICON.running}
          <span className="text-xs font-bold text-orange-400">{label}</span>
          <span className="text-xs text-white flex-1 min-w-0 truncate">{act.task}</span>
          {act.confidence > 0 && <span className="text-[10px] text-zinc-500">conf {Math.round(act.confidence * 100)}%</span>}
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-500">{act.action}</span>
          <ChevronDown className={`w-3 h-3 text-zinc-600 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
        {act.reasoning_summary && <p className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{act.reasoning_summary}</p>}
      </button>
      {open && act.result && (
        <div className="mt-2 space-y-2">
          {act.result.output && (
            <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap max-h-60 overflow-y-auto bg-black/30 rounded-lg p-3">{act.result.output}</pre>
          )}
          {act.result.artifact?.url && (
            <img src={act.result.artifact.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${act.result.artifact.url}` : act.result.artifact.url}
              alt="Generated asset" className="rounded-lg max-h-48" />
          )}
          {act.result.evidence?.length > 0 && (
            <div className="text-[10px] text-zinc-500">
              <span className="uppercase tracking-wider text-zinc-600">Evidence: </span>
              {act.result.evidence.join(' · ')}
            </div>
          )}
          {act.result.escalation_reason && (
            <p className="text-[11px] text-purple-400">Escalated: {act.result.escalation_reason}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function AgentWorkforcePage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [agents, setAgents] = useState(null);
  const [objective, setObjective] = useState('');
  const [activeRun, setActiveRun] = useState(null);
  const [runs, setRuns] = useState([]);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/autonomy/workforce`).then(r => setAgents(r.data.agents)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/autonomy/workforce/runs`, { headers });
      setRuns(data);
    } catch { /* transient */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  const pollRun = useCallback(async (runId) => {
    try {
      const { data } = await axios.get(`${API}/autonomy/workforce/runs/${runId}`, { headers });
      setActiveRun(data);
      return data.status;
    } catch { return 'failed'; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!activeRun || !['planning', 'running'].includes(activeRun.status)) return;
    const iv = setInterval(async () => {
      const status = await pollRun(activeRun.id);
      if (!['planning', 'running'].includes(status)) { clearInterval(iv); loadRuns(); }
    }, 4000);
    return () => clearInterval(iv);
    // Granular deps intentional: poll only re-arms when the run id/status changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?.id, activeRun?.status, pollRun, loadRuns]);

  const startRun = async () => {
    if (!objective.trim()) return toast.error('Describe your objective first');
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/autonomy/workforce/run`, { objective }, { headers });
      toast.success('Delphi Core is coordinating the workforce…');
      setActiveRun({ ...data, activities: [] });
      setObjective('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start run'); }
    finally { setStarting(false); }
  };

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white pb-16" data-testid="workforce-page">
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
            <Network className="w-6 h-6 text-orange-400" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="workforce-title">Agent Workforce</h1>
              <p className="text-sm text-white/40">12 specialized agents coordinated by Delphi Core — Chief Intelligence</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* Objective launcher */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="objective-section">
            <p className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-3">Give Delphi Core an objective</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={objective} onChange={e => setObjective(e.target.value)}
                placeholder='e.g. "Grow my audience by 20%"'
                onKeyDown={e => e.key === 'Enter' && startRun()}
                className="bg-zinc-800/60 border-zinc-700 flex-1" data-testid="objective-input" />
              <Button onClick={startRun} disabled={starting}
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
                data-testid="run-workforce-btn">
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-2" />Run Workforce</>}
              </Button>
            </div>
          </div>

          {/* Active run — Agent Activity */}
          {activeRun && (
            <div className="bg-zinc-900/60 border border-orange-500/20 rounded-2xl p-5" data-testid="active-run">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-orange-400" />
                <p className="text-sm font-semibold text-white flex-1 min-w-0 truncate">{activeRun.objective}</p>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                  activeRun.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400'
                  : activeRun.status === 'failed' ? 'bg-red-500/10 text-red-400'
                  : 'bg-orange-500/10 text-orange-400'}`} data-testid="run-status">{activeRun.status}</span>
                <button onClick={() => pollRun(activeRun.id)} className="text-zinc-500 hover:text-white" data-testid="refresh-run">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-2" data-testid="activity-feed">
                {(activeRun.activities || []).map(a => <ActivityRow key={a.id} act={a} agents={agents} />)}
                {['planning', 'running'].includes(activeRun.status) && (
                  <div className="flex items-center gap-2 text-xs text-zinc-500 p-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" />Agents are working…
                  </div>
                )}
              </div>
              {activeRun.final_summary && (
                <div className="mt-4 p-4 bg-orange-500/[0.06] border border-orange-500/20 rounded-xl" data-testid="final-summary">
                  <p className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-2">Delphi Core — Strategic Synthesis</p>
                  <pre className="text-xs text-zinc-300 whitespace-pre-wrap">{activeRun.final_summary}</pre>
                </div>
              )}
            </div>
          )}

          {/* Roster */}
          <div data-testid="roster-section">
            <p className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-3">The Workforce</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {agents && Object.entries(agents).map(([id, a]) => (
                <div key={id} className={`p-4 rounded-xl border ${id === 'delphi_core' ? 'border-orange-500/40 bg-orange-500/[0.06]' : 'border-zinc-800 bg-zinc-900/50'}`}
                  data-testid={`agent-card-${id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Bot className={`w-4 h-4 ${id === 'delphi_core' ? 'text-orange-400' : 'text-zinc-500'}`} />
                    <p className="text-sm font-semibold text-white">{a.label}</p>
                  </div>
                  <p className="text-[11px] text-zinc-500 leading-snug mb-2">{a.role}</p>
                  <div className="flex flex-wrap gap-1">
                    {a.capabilities.slice(0, 4).map(c => (
                      <span key={c} className="px-1.5 py-0.5 text-[9px] bg-zinc-800 text-zinc-400 rounded">{c}</span>
                    ))}
                    {a.capabilities.length > 4 && <span className="text-[9px] text-zinc-600">+{a.capabilities.length - 4}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Past runs */}
          {runs.length > 0 && (
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5" data-testid="runs-section">
              <p className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-3">Past Runs</p>
              <div className="space-y-1">
                {runs.map(r => (
                  <button key={r.id} onClick={() => pollRun(r.id)}
                    className="w-full flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-zinc-800/50 text-left"
                    data-testid={`run-row-${r.id}`}>
                    {STATUS_ICON[r.status === 'completed' ? 'completed' : r.status === 'failed' ? 'failed' : 'running']}
                    <span className="text-xs text-white flex-1 truncate">{r.objective}</span>
                    <span className="text-[10px] text-zinc-600">{new Date(r.created_at).toLocaleString()}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
